import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApiQuery } from "@/lib/useApiQuery";
import { downloadCsv } from "@/lib/csv";
import { ArrowLeft, ChevronRight, Download, Search, Zap, Cog } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { usePersistentState } from "@/lib/usePersistentState";

const AXIS_TICK = { fontSize: 11, fontFamily: "Open Sans" };
const TOOLTIP_STYLE = { background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 6, fontFamily: "Open Sans", fontSize: 12 };
const TOOLTIP_LABEL_STYLE = { color: "#6B7280" };
const RANGE_OPTIONS = [1, 7, 30];

function extractNumber(val) {
  if (val == null) return null;
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const m = val.match(/-?\d+(\.\d+)?/);
    return m ? Number(m[0]) : null;
  }
  if (typeof val === "object" && val.val != null) return Number(val.val);
  return null;
}
function extractUnit(val) {
  if (val && typeof val === "object" && val.unit) return val.unit;
  if (typeof val === "string") {
    const m = val.replace(/-?\d+(\.\d+)?/, "").trim();
    return m || "";
  }
  return "";
}

function HistoryChart({ pointId }) {
  const [days, setDays] = useState(7);
  const { data, loading, err } = useApiQuery(
    ["pointHistory", pointId, days], `/points/${encodeURIComponent(pointId)}/history`,
    { params: { days }, enabled: Boolean(pointId) }
  );

  const chartData = useMemo(
    () => (data?.series || [])
      .map((row) => ({ ts: row.ts, val: extractNumber(row.val) }))
      .filter((r) => r.val != null),
    [data]
  );

  return (
    <div className="k-card p-6" data-testid="history-chart">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="k-label">History</div>
          <div className="text-lg font-heading font-semibold text-[#212121]">Time-series (last {days}d)</div>
        </div>
        <div className="flex gap-2">
          {RANGE_OPTIONS.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`history-range-${d}d`}
              className={`px-3 py-1 text-xs font-heading font-semibold rounded-md border ${days === d ? "border-[#156F5B] bg-[#156F5B]/10 text-[#156F5B]" : "border-[#E5E7EB] bg-white text-[#6B7280] hover:text-[#212121]"}`}
              style={{ transition: "color .2s ease, border-color .2s ease" }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <div className="mt-6 h-72">
        {loading ? (
          <div className="h-full flex items-center justify-center k-sublabel">Loading…</div>
        ) : err ? (
          <div className="text-red-600 text-sm">{err}</div>
        ) : chartData.length === 0 ? (
          <div className="text-[#6B7280] text-sm">No history available for this point.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#EBF1EF" strokeDasharray="4 4" />
              <XAxis
                dataKey="ts"
                stroke="#9CA3AF"
                tick={AXIS_TICK}
                tickFormatter={(v) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "")}
                minTickGap={30}
              />
              <YAxis stroke="#9CA3AF" tick={AXIS_TICK} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                labelFormatter={(v) => new Date(v).toLocaleString()}
              />
              <Line type="monotone" dataKey="val" stroke="#156F5B" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default function SiteDetailPage() {
  const { siteId } = useParams();
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [query, setQuery] = usePersistentState("koioc_point_search", "");

  const { data: site, loading: loadingSite } = useApiQuery(["site", siteId], `/sites/${encodeURIComponent(siteId)}`);
  const { data: pointData, loading: loadingPoints } = useApiQuery(
    ["sitePoints", siteId], `/sites/${encodeURIComponent(siteId)}/points`
  );
  const points = useMemo(() => pointData || [], [pointData]);
  const loading = loadingSite || loadingPoints;

  useEffect(() => {
    if (!points.length || selectedPoint) return;
    const firstHis = points.find((pt) => (pt.raw?.his || pt.tags?.includes("his")) && extractNumber(pt.cur_val) != null);
    setSelectedPoint(firstHis?.point_id || points[0]?.point_id || null);
  }, [points, selectedPoint]);

  if (loading) {
    return <div className="k-sublabel">Loading site…</div>;
  }
  if (!site) return <div className="text-red-600">Site not found.</div>;

  const q = query.trim().toLowerCase();
  const filteredPoints = !q
    ? points
    : points.filter((p) =>
        [p.dis, p.unit, p.kind, p.equip_ref].some((v) => v && String(v).toLowerCase().includes(q))
      );

  const kpiPoints = points.filter((p) => extractNumber(p.cur_val) != null).slice(0, 4);
  const numericPoints = filteredPoints.filter((p) => extractNumber(p.cur_val) != null).slice(0, 100);
  const location = [site.geo_city, site.geo_state, site.geo_country].filter(Boolean).join(" • ");

  return (
    <div className="max-w-7xl fade-up">
      <div className="flex items-center gap-2 text-xs text-[#6B7280]">
        <Link to="/sites" className="hover:text-[#156F5B] font-heading font-semibold inline-flex items-center gap-1" data-testid="back-to-sites">
          <ArrowLeft className="h-3 w-3" /> Sites
        </Link>
        <ChevronRight className="h-3 w-3" />
        <span className="font-heading font-semibold text-[#212121]">{site.dis}</span>
      </div>

      <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="k-label">Site</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]">{site.dis}</h1>
          <div className="mt-2 text-[#4D4D4D] text-sm">{location}</div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 justify-end">
            <Link
              to={`/sites/${encodeURIComponent(site.site_id)}/equipment`}
              className="k-btn-outline inline-flex"
              data-testid="site-equipment-link"
            >
              <Cog className="h-3.5 w-3.5" /> Equipment
            </Link>
            <Link
              to={`/sites/${encodeURIComponent(site.site_id)}/energy`}
              className="k-btn !py-2 !px-4 !text-xs inline-flex"
              data-testid="site-energy-link"
            >
              <Zap className="h-3.5 w-3.5" /> Energy &amp; cost
            </Link>
          </div>
          <div className="mt-3 k-sublabel">Site ID</div>
          <div className="text-xs text-[#9CA3AF] break-all max-w-xs">{site.site_id}</div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {kpiPoints.map((p, i) => (
          <div key={p.point_id} className="col-span-1 md:col-span-2 k-card p-6" data-testid={`kpi-point-${i}`}>
            <div className="k-sublabel truncate">{p.dis}</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-4xl font-semibold font-heading text-[#156F5B]">
                {extractNumber(p.cur_val)?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
              <span className="text-xs text-[#6B7280] font-heading">{p.unit || extractUnit(p.cur_val)}</span>
            </div>
            <div className="mt-2 k-sublabel">{p.kind}</div>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <Tabs defaultValue="chart">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <TabsList className="bg-white border border-[#E5E7EB] p-1 rounded-md">
              <TabsTrigger value="chart" data-testid="tab-chart" className="text-xs font-heading font-semibold rounded data-[state=active]:bg-[#156F5B]/10 data-[state=active]:text-[#156F5B]">Chart</TabsTrigger>
              <TabsTrigger value="table" data-testid="tab-table" className="text-xs font-heading font-semibold rounded data-[state=active]:bg-[#156F5B]/10 data-[state=active]:text-[#156F5B]">Readings</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search points, unit, equipment…"
                  data-testid="point-search-input"
                  className="k-input !w-64 !pl-9 !py-2 text-xs"
                />
              </div>
              {q && (
                <span className="k-sublabel" data-testid="point-search-count">
                  {filteredPoints.length} of {points.length}
                </span>
              )}
              <button
                onClick={() =>
                  downloadCsv(
                    `${(site.dis || site.site_id).replace(/[^a-z0-9-_]+/gi, "_")}_points.csv`,
                    ["Point", "Kind", "Unit", "Current Value", "Status", "Point ID"],
                    filteredPoints.map((p) => [
                      p.dis,
                      p.kind,
                      p.unit || extractUnit(p.cur_val),
                      extractNumber(p.cur_val) ?? p.cur_val,
                      p.cur_status,
                      p.point_id,
                    ])
                  )
                }
                data-testid="site-export-csv-btn"
                className="k-btn-outline"
              >
                <Download className="h-3 w-3" /> Export CSV
              </button>
            </div>
          </div>
          <TabsContent value="chart" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 k-card p-4 h-fit">
                <div className="k-sublabel mb-3">Select point</div>
                <div className="max-h-96 overflow-auto space-y-1">
                  {numericPoints.map((p, i) => (
                    <button
                      key={p.point_id}
                      onClick={() => setSelectedPoint(p.point_id)}
                      data-testid={`point-select-${i}`}
                      className={`w-full text-left px-3 py-2 text-xs rounded-md ${selectedPoint === p.point_id ? "bg-[#156F5B]/10 text-[#156F5B]" : "text-[#4D4D4D] hover:bg-[#F3F8F7]"}`}
                      style={{ transition: "color .2s ease, background-color .2s ease" }}
                    >
                      <div className="truncate font-heading font-semibold">{p.dis}</div>
                      <div className="text-[11px] text-[#9CA3AF] truncate">{p.kind} • {p.unit || extractUnit(p.cur_val)}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2">
                {selectedPoint ? <HistoryChart pointId={selectedPoint} /> : <div className="text-[#6B7280]">Select a point to view history.</div>}
              </div>
            </div>
          </TabsContent>
          <TabsContent value="table" className="mt-6">
            <div className="k-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E5E7EB] hover:bg-transparent bg-[#F9FAFB]">
                    <TableHead className="k-sublabel">Point</TableHead>
                    <TableHead className="k-sublabel">Kind</TableHead>
                    <TableHead className="k-sublabel">Unit</TableHead>
                    <TableHead className="k-sublabel text-right">Current</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="readings-table">
                  {filteredPoints.length === 0 ? (
                    <TableRow className="border-[#F0F2F1]">
                      <TableCell colSpan={4} className="text-sm text-[#6B7280] text-center py-8" data-testid="no-matching-points">
                        No points match &quot;{query}&quot;.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPoints.slice(0, 200).map((p, i) => (
                      <TableRow key={p.point_id} className="border-[#F0F2F1] hover:bg-[#F9FAFB]" data-testid={`reading-row-${i}`}>
                        <TableCell className="text-sm text-[#212121]">{p.dis}</TableCell>
                        <TableCell className="text-xs text-[#6B7280]">{p.kind || "—"}</TableCell>
                        <TableCell className="text-xs text-[#6B7280]">{p.unit || "—"}</TableCell>
                        <TableCell className="text-sm font-heading font-semibold text-[#156F5B] text-right">
                          {extractNumber(p.cur_val) != null ? extractNumber(p.cur_val).toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(p.cur_val ?? "—")}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
